import React from 'react';
import type { PropsWithChildren } from 'react';
import { useRouterState } from '@tanstack/react-router';

import { useAuth } from '../Auth';
import { Config } from '../Config';
import { Loader } from '../../components/common/Loader';

import { SiteRoutesContext } from './SiteRoutesContext';
import { Routes } from './Routes';
import { isSecureRoute } from './isSecureRoute';

export const SiteRoutesProvider: React.FC<PropsWithChildren<{}>> = (props) => {
  const currentPath = useRouterState({
    select: (state) => {
      return state.location.pathname;
    },
  });
  const auth = useAuth();
  const logout = auth.logout;
  const isAuthenticated = auth.isAuthenticated;
  const isAuthLoading = auth.isLoading;
  const shouldProtectSecureRoutes = !Config.APPLICATION_FEATURE_USE_DEVLAUNCHER;
  const isOnSecureRoute = isSecureRoute(currentPath);

  React.useEffect(() => {
    if (!shouldProtectSecureRoutes) {
      return;
    }

    if (isAuthLoading) {
      return;
    }

    if (!isAuthenticated && isOnSecureRoute) {
      logout({ reasonCode: 'SESSION_EXPIRED' });
    }
  }, [
    isOnSecureRoute,
    isAuthenticated,
    isAuthLoading,
    logout,
    shouldProtectSecureRoutes,
  ]);

  // Block rendering on secure routes when unauthenticated to prevent
  // page content flashing before the logout redirect completes.
  if (shouldProtectSecureRoutes && isOnSecureRoute && !isAuthenticated) {
    return <Loader label="Authenticating" />;
  }

  return (
    <SiteRoutesContext.Provider value={{ routes: Routes, currentPath }}>
      {props.children}
    </SiteRoutesContext.Provider>
  );
};
